import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../utils/dateUtils';
import {
  jobWorkApiService,
  JobWorkOrderDto,
  jobWorkUsesRawItem,
} from '../services/jobWorkApi';
import { PermissionGate } from './ui/PermissionGate';

const JOBWORK_ORDERS_ROUTE = '/jobwork/orders';

function statusBadgeClass(status?: string): string {
  switch (String(status ?? '').toUpperCase()) {
    case 'DRAFT':
      return 'bg-gray-100 text-gray-800 border-gray-300';
    case 'APPROVED':
    case 'RELEASED':
      return 'bg-blue-100 text-blue-800 border-blue-300';
    case 'PARTIAL':
      return 'bg-amber-100 text-amber-800 border-amber-300';
    case 'COMPLETED':
    case 'CLOSED':
      return 'bg-green-100 text-green-800 border-green-300';
    case 'CANCELLED':
      return 'bg-red-100 text-red-800 border-red-300';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-300';
  }
}

function formatQty(value?: number | null) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

export function JobWorkOrderViewPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [order, setOrder] = useState<JobWorkOrderDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const loadOrder = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await jobWorkApiService.getOrderById(id);
      if (res.success && res.data) {
        setOrder(res.data);
      } else {
        setOrder(null);
        toast.error('Job work order not found');
      }
    } catch {
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void loadOrder();
  }, [loadOrder]);

  const handleApprove = async () => {
    if (!id) return;
    try {
      setActionLoading(true);
      const res = await jobWorkApiService.approveOrder(id, user?.id);
      if (res.success) await loadOrder();
    } catch {
      // Backend error already toasted by API service
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-6 space-y-4">
        <Button variant="outline" onClick={() => navigate('/jobwork/orders')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <p className="text-gray-600">Job work order not found.</p>
      </div>
    );
  }

  const status = order.status ?? 'DRAFT';
  const canApprove = status === 'DRAFT';

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6 max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button variant="outline" onClick={() => navigate('/jobwork/orders')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to list
          </Button>
          <div className="flex flex-wrap gap-2">
            {canApprove ? (
              <PermissionGate route={JOBWORK_ORDERS_ROUTE} action="edit">
                <Button
                  onClick={() => void handleApprove()}
                  disabled={actionLoading}
                  className="bg-primary-600 hover:bg-primary-700 text-white"
                >
                  {actionLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  Approve
                </Button>
              </PermissionGate>
            ) : null}
          </div>
        </div>

        <Card className="border-primary-200 shadow-md">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle className="text-xl">{order.jwOrderNo || `JW Order #${order.id}`}</CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  {order.branchName || (order.branchId != null ? `Branch #${order.branchId}` : '—')}
                </p>
              </div>
              <Badge variant="outline" className={statusBadgeClass(status)}>
                {status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-gray-500">Party type</div>
                <div className="font-medium">{order.partyType}</div>
              </div>
              <div>
                <div className="text-gray-500">
                  {order.partyType === 'VENDOR' ? 'Supplier' : 'To branch'}
                </div>
                <div className="font-medium">
                  {order.partyType === 'VENDOR'
                    ? order.supplierName ||
                      (order.supplierId != null ? `#${order.supplierId}` : '—')
                    : order.toBranchName ||
                      (order.toBranchId != null ? `#${order.toBranchId}` : '—')}
                </div>
              </div>
              <div>
                <div className="text-gray-500">Order date</div>
                <div className="font-medium">
                  {order.orderDate ? formatDate(order.orderDate) : '—'}
                </div>
              </div>
              <div>
                <div className="text-gray-500">Expected return</div>
                <div className="font-medium">
                  {order.expectedReturnDate ? formatDate(order.expectedReturnDate) : '—'}
                </div>
              </div>
              <div>
                <div className="text-gray-500">Endgood</div>
                <div className="font-medium">
                  {order.endgoodName ||
                    (order.endgoodId != null ? `#${order.endgoodId}` : '—')}
                </div>
              </div>
              <div>
                <div className="text-gray-500">Process</div>
                <div className="font-medium">
                  {order.processName ||
                    (order.processId != null ? `#${order.processId}` : '—')}
                </div>
              </div>
              <div>
                <div className="text-gray-500">Work orders</div>
                <div className="font-medium">
                  {order.workOrders?.length
                    ? order.workOrders
                        .map((wo) => {
                          const label = wo.workOrderNumber || `#${wo.workOrderId}`;
                          return `${label} (${wo.qty})`;
                        })
                        .join(', ')
                    : '—'}
                </div>
              </div>
              <div className="md:col-span-2 lg:col-span-3">
                <div className="text-gray-500">Notes</div>
                <div className="font-medium whitespace-pre-wrap">{order.notes || '—'}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-primary-200 shadow-md">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b">
            <CardTitle className="text-lg">Issue lines</CardTitle>
          </CardHeader>
          <CardContent className="p-4 overflow-x-auto">
            {!order.issueLines?.length ? (
              <p className="text-gray-500 py-4 text-center">No issue lines.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Job type</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead className="text-right">Planned</TableHead>
                    <TableHead className="text-right">Issued</TableHead>
                    <TableHead className="text-right">Received back</TableHead>
                    <TableHead className="text-right">Scrap</TableHead>
                    <TableHead className="text-right">Open at JW</TableHead>
                    <TableHead>UOM</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {order.issueLines.map((line, idx) => (
                    <TableRow key={line.id ?? idx}>
                      <TableCell>{line.jobType}</TableCell>
                      <TableCell>
                        {jobWorkUsesRawItem(line.jobType)
                          ? line.rawItemName ||
                            (line.rawItemId != null ? `Raw #${line.rawItemId}` : '—')
                          : line.endgoodName ||
                            (line.endgoodId != null ? `EG #${line.endgoodId}` : '—')}
                      </TableCell>
                      <TableCell className="text-right">{formatQty(line.plannedQty)}</TableCell>
                      <TableCell className="text-right">{formatQty(line.issuedQty)}</TableCell>
                      <TableCell className="text-right">{formatQty(line.receivedBackQty)}</TableCell>
                      <TableCell className="text-right">{formatQty(line.scrapQty)}</TableCell>
                      <TableCell className="text-right">{formatQty(line.openAtJwQty)}</TableCell>
                      <TableCell>{line.uom || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card className="border-primary-200 shadow-md">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b">
            <CardTitle className="text-lg">Output lines</CardTitle>
          </CardHeader>
          <CardContent className="p-4 overflow-x-auto">
            {!order.outputLines?.length ? (
              <p className="text-gray-500 py-4 text-center">No output lines.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Job type</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>End process</TableHead>
                    <TableHead className="text-right">Planned</TableHead>
                    <TableHead className="text-right">Received</TableHead>
                    <TableHead>UOM</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {order.outputLines.map((line, idx) => (
                    <TableRow key={line.id ?? idx}>
                      <TableCell>{line.jobType || '—'}</TableCell>
                      <TableCell>
                        {line.jobType && jobWorkUsesRawItem(line.jobType)
                          ? line.rawItemName ||
                            (line.rawItemId != null ? `Raw #${line.rawItemId}` : '—')
                          : line.endgoodName ||
                            (line.endgoodId != null ? `EG #${line.endgoodId}` : '—')}
                      </TableCell>
                      <TableCell>
                        {line.processName ||
                          (line.processId != null ? `#${line.processId}` : '—')}
                      </TableCell>
                      <TableCell className="text-right">{formatQty(line.plannedQty)}</TableCell>
                      <TableCell className="text-right">{formatQty(line.receivedQty)}</TableCell>
                      <TableCell>{line.uom || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default JobWorkOrderViewPage;
